import axios from "axios";
import { toast } from "sonner";
import { API, reviveField } from "@/lib/api";
import { allOps, removeOp, updateOp, addFailed } from "@/lib/offlineQueue";
import { SYNC, setSyncState } from "@/lib/syncState";

// Raw client WITHOUT the offline interceptor, to replay queued ops without re-queueing.
const raw = axios.create({ baseURL: API, withCredentials: true });
let syncing = false;

function backoffMs(retry) {
  return Math.min(Math.pow(2, retry) * 1000, 5 * 60 * 1000);
}

// Temp ids of offline-created records -> real server ids (persisted across sync runs).
const MAP_KEY = "offline_id_map";
function loadMap() { try { return JSON.parse(localStorage.getItem(MAP_KEY) || "{}"); } catch (_) { return {}; } }
function saveMap(m) { try { localStorage.setItem(MAP_KEY, JSON.stringify(m)); } catch (_) {} }
function swapIds(s, map) {
  return typeof s === "string" && s.includes("op_") ? s.replace(/op_[a-z0-9]+_[a-f0-9]+/g, (t) => map[t] || t) : s;
}
function resolveOp(op, map) {
  return {
    ...op,
    url: swapIds(op.url, map),
    body: swapIds(op.body, map),
    fields: op.fields ? op.fields.map(([k, v]) => [k, swapIds(v, map)]) : op.fields,
  };
}

// Re-send a failed photo upload kept in the queue.
export async function retryUpload(operationId) {
  const op = (await allOps()).find((o) => o.operationId === operationId);
  if (!op) return;
  await updateOp({ ...op, status: "pending", retryCount: 0, nextAttempt: 0, lastError: null, updatedAt: Date.now() });
  window.dispatchEvent(new Event("offline-queued"));
  await flushQueue();
}

export async function flushQueue() {
  if (syncing || !navigator.onLine) return;
  syncing = true;
  let hadError = false;
  try {
    const ops = (await allOps()).sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    const due = ops.filter((o) => o.status !== "failed" && (!o.nextAttempt || o.nextAttempt <= Date.now()));
    if (due.length === 0) { setSyncState({ pending: ops.length }); return; }
    setSyncState({ status: SYNC.SYNCING, pending: ops.length, error: null });
    // Keep the SYNCING indicator visible briefly so the user perceives the sync feedback.
    await new Promise((r) => setTimeout(r, 500));
    const idMap = loadMap();
    const allQueued = await allOps();
    const failedParents = new Set();
    for (const queued of due) {
      const op = resolveOp(queued, idMap);
      // Parent created offline but not on the server yet (failed now / in backoff): wait, don't send with a temp id.
      const refs = (JSON.stringify([op.url, op.body, (op.fields || []).filter(([, v]) => typeof v === "string")]).match(/op_[a-z0-9]+_[a-f0-9]+/g) || [])
        .filter((t) => t !== op.entityId);
      if (refs.some((t) => failedParents.has(t) || allQueued.some((o) => o.op === "create" && o.entityId === t && !idMap[t] && o.operationId !== op.operationId && o.status !== "failed"))) {
        if (op.op === "create") failedParents.add(op.entityId);
        hadError = true;
        continue;
      }
      const headers = { "Idempotency-Key": op.idempotencyKey || op.operationId };
      const method = (op.method || "post").toLowerCase();
      try {
        let res;
        if (op.type === "form") {
          const fd = new FormData();
          (op.fields || []).forEach(([k, v]) => {
            const val = reviveField(v);
            if (val && typeof val !== "string" && typeof val.size === "number") fd.append(k, val, val.name || "foto.jpg");
            else if (val != null && val !== "" && !((k === "lat" || k === "lng") && !Number.isFinite(Number(val)))) fd.append(k, val);
          });
          res = await raw.request({ url: op.url, method, data: fd, headers });
        } else if (method === "delete") {
          res = await raw.request({ url: op.url, method, headers });
        } else {
          res = await raw.request({ url: op.url, method, data: op.body,
            headers: { ...headers, "Content-Type": "application/json" } });
        }
        if (op.op === "create" && op.entityId && res?.data?.id && res.data.id !== op.entityId) {
          idMap[op.entityId] = res.data.id; saveMap(idMap);
        }
        await removeOp(op.operationId);
      } catch (e) {
        const status = e.response && e.response.status;
        if (status && status !== 401 && status < 500) {
          // Permanent client error (400/404/409, …): drop from the active queue so it can't
          // block sync, but keep it in the "failed" store so the user still sees it.
          let detail = "";
          try {
            const d = e.response && e.response.data && e.response.data.detail;
            detail = typeof d === "string" ? d : (d ? JSON.stringify(d) : "");
          } catch (_) {}
          if (op.type === "form") {
            // Photo/file upload: keep it in the queue (visible at the defect + Fotos page, retryable).
            await updateOp({ ...op, status: "failed", failureStatus: status, lastError: detail || `HTTP ${status}`, updatedAt: Date.now() });
            try { toast.error("Ein Foto konnte nicht übertragen werden – bitte am Foto „Erneut senden“ tippen."); } catch (_) {}
          } else {
            await removeOp(op.operationId);
            await addFailed({ ...op, failedAt: Date.now(), failureStatus: status, lastError: detail || `HTTP ${status}` });
            try { toast.warning("Eine offline gespeicherte Änderung konnte nicht übernommen werden."); } catch (_) {}
          }
          window.dispatchEvent(new Event("offline-failed"));
        } else {
          // Network error / expired session (401) / server error (5xx): keep + backoff.
          const retry = (op.retryCount || 0) + 1;
          await updateOp({ ...op, retryCount: retry, status: "error", updatedAt: Date.now(),
            nextAttempt: Date.now() + backoffMs(retry), lastError: String((e && e.message) || e) });
          hadError = true;
          if (op.op === "create") failedParents.add(op.entityId);
        }
      }
      window.dispatchEvent(new Event("offline-queued"));
    }
  } finally {
    syncing = false;
    const remaining = (await allOps()).length;
    const status = hadError
      ? SYNC.SYNC_ERROR
      : (navigator.onLine ? (remaining ? SYNC.ONLINE : SYNC.SYNCED) : SYNC.OFFLINE);
    setSyncState({ status, pending: remaining, lastSync: Date.now() });
    window.dispatchEvent(new Event("offline-queued"));
  }
}
